import React, { useState, useEffect, useCallback } from 'react';
import {
  BookOpen,
  Heart,
  Search,
  ChevronLeft,
  ChevronRight,
  Bookmark,
  PenLine,
  Star,
  Calendar
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import Button from '../components/Button';
import devotionalService, { DevotionalReading, DevotionalFavorite, DevotionalJournalEntry } from '../services/devotionalService';

type Tab = 'hoje' | 'diario' | 'salvos' | 'buscar';

function formatDateParam(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDateDisplay(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  return `${d} ${months[m - 1]} ${y}`;
}

const Devocional: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('hoje');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [reading, setReading] = useState<DevotionalReading | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFavorited, setIsFavorited] = useState(false);
  const [journalContent, setJournalContent] = useState('');
  const [journalSaving, setJournalSaving] = useState(false);
  const [journalSaved, setJournalSaved] = useState(false);
  const [favorites, setFavorites] = useState<DevotionalFavorite[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<DevotionalReading[]>([]);
  const [searching, setSearching] = useState(false);
  const [journalEntries, setJournalEntries] = useState<DevotionalJournalEntry[]>([]);

  const loadReading = useCallback(async (date: Date) => {
    setLoading(true);
    setError('');
    try {
      const dateStr = formatDateParam(date);
      const data = await devotionalService.getReading(dateStr);
      setReading(data);

      // Load journal entry for this reading
      if (data?.id) {
        const entry = await devotionalService.getJournalEntry(data.id);
        setJournalContent(entry?.content || '');
        setJournalSaved(false);

        // Check if favorited
        const favs = await devotionalService.getFavorites();
        setIsFavorited(favs.some(f => f.reading_id === data.id));
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar leitura do dia');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReading(currentDate);
  }, [currentDate, loadReading]);

  const goToPreviousDay = () => {
    const prev = new Date(currentDate);
    prev.setDate(prev.getDate() - 1);
    setCurrentDate(prev);
  };

  const goToNextDay = () => {
    const next = new Date(currentDate);
    next.setDate(next.getDate() + 1);
    const today = new Date();
    if (next <= today) {
      setCurrentDate(next);
    }
  };

  const handleToggleFavorite = async () => {
    if (!reading?.id) return;
    try {
      const result = await devotionalService.toggleFavorite(reading.id);
      setIsFavorited(result.favorited);
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  const handleSaveJournal = async () => {
    if (!reading?.id || !journalContent.trim()) return;
    setJournalSaving(true);
    try {
      await devotionalService.saveJournalEntry(reading.id, journalContent);
      setJournalSaved(true);
      setTimeout(() => setJournalSaved(false), 3000);
    } catch (err) {
      console.error('Error saving journal:', err);
    } finally {
      setJournalSaving(false);
    }
  };

  const loadJournalEntries = async () => {
    try {
      const entries = await devotionalService.getJournalEntries();
      setJournalEntries(entries);
    } catch (err) {
      console.error('Error loading journal entries:', err);
    }
  };

  const loadFavorites = async () => {
    try {
      const favs = await devotionalService.getFavorites();
      setFavorites(favs);
    } catch (err) {
      console.error('Error loading favorites:', err);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) return;
    setSearching(true);
    try {
      const results = await devotionalService.searchReadings(searchQuery);
      setSearchResults(results);
    } catch (err) {
      console.error('Error searching:', err);
    } finally {
      setSearching(false);
    }
  };

  const navigateToReading = (readingDate: string) => {
    const [y, m, d] = readingDate.split('-').map(Number);
    setCurrentDate(new Date(y, m - 1, d));
    setActiveTab('hoje');
  };

  const isToday = formatDateParam(currentDate) === formatDateParam(new Date());

  const tabs: { id: Tab; label: string; icon: React.ElementType }[] = [
    { id: 'hoje', label: 'Hoje', icon: BookOpen },
    { id: 'diario', label: 'Diário', icon: PenLine },
    { id: 'salvos', label: 'Salvos', icon: Heart },
    { id: 'buscar', label: 'Buscar', icon: Search },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 via-cream-100 to-gold-100">
      <Sidebar currentPath="/devocional" />

      <div className="ml-0 md:ml-64 pt-16 md:pt-0 p-4 md:p-8">
        <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-brown-800">Devocional</h1>
          <p className="text-brown-600 mt-1">Sua leitura espiritual diária</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === 'diario') loadJournalEntries();
                  if (tab.id === 'salvos') loadFavorites();
                }}
                className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap text-sm md:text-base ${
                  isActive
                    ? 'bg-gold-500 text-white'
                    : 'bg-white text-brown-700 hover:bg-gold-50 border border-cream-300'
                }`}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab: Hoje */}
        {activeTab === 'hoje' && (
          <div>
            {/* Date Navigation */}
            <div className="bg-white rounded-2xl p-6 shadow-md border border-cream-200 mb-6">
              <div className="flex items-center justify-between">
                <button
                  onClick={goToPreviousDay}
                  className="p-2 rounded-lg hover:bg-cream-100 text-brown-600 transition-colors"
                >
                  <ChevronLeft size={24} />
                </button>

                <div className="text-center">
                  <p className="text-sm text-brown-500">
                    {reading?.weekday} {reading?.liturgical_season ? `\u2022 ${reading.liturgical_season}` : ''}
                  </p>
                  <h2 className="text-2xl font-bold text-brown-800">
                    {reading ? formatDateDisplay(reading.date) : formatDateDisplay(formatDateParam(currentDate))}
                  </h2>
                  {reading?.liturgical_title && reading.liturgical_title !== reading.liturgical_season && (
                    <p className="text-sm text-brown-600">{reading.liturgical_title}</p>
                  )}
                </div>

                <button
                  onClick={goToNextDay}
                  disabled={isToday}
                  className={`p-2 rounded-lg transition-colors ${
                    isToday ? 'text-cream-300 cursor-not-allowed' : 'hover:bg-cream-100 text-brown-600'
                  }`}
                >
                  <ChevronRight size={24} />
                </button>
              </div>

              {/* Favorite button */}
              {reading && (
                <div className="flex justify-center mt-4">
                  <button
                    onClick={handleToggleFavorite}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      isFavorited
                        ? 'bg-gold-100 text-gold-700'
                        : 'bg-cream-50 text-brown-500 hover:bg-cream-100'
                    }`}
                  >
                    <Bookmark size={18} className={isFavorited ? 'fill-current' : ''} />
                    {isFavorited ? 'Salvo nos favoritos' : 'Salvar nos favoritos'}
                  </button>
                </div>
              )}
            </div>

            {loading && (
              <div className="bg-white rounded-2xl p-12 shadow-md border border-cream-200 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold-500 mx-auto mb-4"></div>
                <p className="text-brown-600">Carregando leitura do dia...</p>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-6">
                <p className="text-red-700">{error}</p>
              </div>
            )}

            {!loading && reading && (
              <>
                {/* Santo do dia */}
                {reading.saint_name && (
                  <div className="bg-white rounded-2xl p-6 shadow-md border border-cream-200 mb-6">
                    <div className="flex items-start gap-4">
                      {reading.saint_image_url && (
                        <img
                          src={reading.saint_image_url}
                          alt={reading.saint_name}
                          className="w-20 h-20 rounded-xl object-cover"
                        />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Star size={16} className="text-gold-500" />
                          <span className="text-xs font-semibold uppercase tracking-wide text-gold-600">Santo do dia</span>
                        </div>
                        <h3 className="text-lg font-bold text-brown-800">{reading.saint_name}</h3>
                        {reading.saint_description && (
                          <p className="text-sm text-brown-600 mt-2 leading-relaxed whitespace-pre-line">
                            {reading.saint_description.length > 400
                              ? reading.saint_description.substring(0, 400) + '...'
                              : reading.saint_description}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Primeira Leitura */}
                {reading.first_reading_text && (
                  <div className="bg-white rounded-2xl p-6 shadow-md border border-cream-200 mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <BookOpen size={16} className="text-gold-500" />
                      <span className="text-xs font-semibold uppercase tracking-wide text-gold-600">Primeira Leitura</span>
                    </div>
                    {reading.first_reading_ref && (
                      <h4 className="text-base font-bold text-brown-800 mb-3">{reading.first_reading_ref}</h4>
                    )}
                    <p className="text-brown-700 leading-relaxed whitespace-pre-line">{reading.first_reading_text}</p>
                  </div>
                )}

                {/* Evangelho */}
                {reading.gospel_text && (
                  <div className="bg-white rounded-2xl p-6 shadow-md border border-cream-200 mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <BookOpen size={16} className="text-gold-500" />
                      <span className="text-xs font-semibold uppercase tracking-wide text-gold-600">Evangelho do Dia</span>
                    </div>
                    {reading.gospel_ref && (
                      <h4 className="text-base font-bold text-brown-800 mb-3">{reading.gospel_ref}</h4>
                    )}
                    <p className="text-brown-700 leading-relaxed whitespace-pre-line">{reading.gospel_text}</p>
                  </div>
                )}

                {/* Comentário Papal */}
                {reading.papal_commentary && (
                  <div className="bg-brown-800 rounded-2xl p-6 shadow-md mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gold-300">Palavras dos Papas</span>
                    </div>
                    <p className="text-cream-100 leading-relaxed whitespace-pre-line">{reading.papal_commentary}</p>
                  </div>
                )}

                {/* Diário Espiritual */}
                <div className="bg-white rounded-2xl p-6 shadow-md border border-cream-200 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <PenLine size={16} className="text-gold-500" />
                      <span className="text-xs font-semibold uppercase tracking-wide text-gold-600">Diário Espiritual</span>
                    </div>
                    {journalSaved && (
                      <span className="text-sm text-green-600 font-medium">Salvo!</span>
                    )}
                  </div>
                  <h4 className="text-base font-bold text-brown-800 mb-3">Escreva sua resposta a Deus</h4>
                  <textarea
                    value={journalContent}
                    onChange={(e) => {
                      setJournalContent(e.target.value);
                      setJournalSaved(false);
                    }}
                    placeholder="O que Deus falou ao seu coração hoje?"
                    className="w-full min-h-[120px] p-4 border border-cream-300 rounded-xl text-brown-700 leading-relaxed resize-none focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-colors"
                    style={{ outline: 'none' }}
                  />
                  <div className="mt-4">
                    <Button
                      onClick={handleSaveJournal}
                      disabled={journalSaving || !journalContent.trim()}
                    >
                      {journalSaving ? 'Salvando...' : 'Salvar no diário'}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Tab: Diário */}
        {activeTab === 'diario' && (
          <div>
            {journalEntries.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 shadow-md border border-cream-200 text-center">
                <PenLine size={48} className="text-cream-300 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-brown-800 mb-2">Seu diário está vazio</h3>
                <p className="text-brown-500">Comece escrevendo sua resposta a Deus na aba "Hoje".</p>
              </div>
            ) : (
              <div className="space-y-4">
                {journalEntries.map(entry => {
                  const r = entry.devotional_readings;
                  return (
                    <div
                      key={entry.id}
                      className="bg-white rounded-2xl p-6 shadow-md border border-cream-200"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          {r && (
                            <button
                              onClick={() => navigateToReading(r.date)}
                              className="text-sm text-gold-600 hover:text-gold-700 font-medium"
                            >
                              {formatDateDisplay(r.date)} — {r.saint_name || r.liturgical_title || r.weekday}
                            </button>
                          )}
                        </div>
                        <span className="text-xs text-brown-400">
                          {entry.updated_at ? new Date(entry.updated_at).toLocaleDateString('pt-BR') : ''}
                        </span>
                      </div>
                      <p className="text-brown-700 leading-relaxed whitespace-pre-line">{entry.content}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Tab: Salvos */}
        {activeTab === 'salvos' && (
          <div>
            {favorites.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 shadow-md border border-cream-200 text-center">
                <Bookmark size={48} className="text-cream-300 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-brown-800 mb-2">Nenhum favorito ainda</h3>
                <p className="text-brown-500">Salve leituras que tocaram seu coração para revisitar depois.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {favorites.map(fav => {
                  const r = fav.devotional_readings;
                  if (!r) return null;
                  return (
                    <button
                      key={fav.id}
                      onClick={() => navigateToReading(r.date)}
                      className="w-full bg-white rounded-2xl p-5 shadow-md border border-cream-200 text-left hover:shadow-lg transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm text-brown-500">{formatDateDisplay(r.date)}</p>
                          <p className="font-bold text-brown-800">{r.liturgical_title || r.weekday}</p>
                          {r.saint_name && (
                            <p className="text-sm text-brown-600 mt-1">{r.saint_name}</p>
                          )}
                          {r.gospel_ref && (
                            <p className="text-xs text-gold-600 mt-1">{r.gospel_ref}</p>
                          )}
                        </div>
                        <Heart size={18} className="text-gold-500 fill-current flex-shrink-0 mt-1" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Tab: Buscar */}
        {activeTab === 'buscar' && (
          <div>
            <div className="bg-white rounded-2xl p-4 md:p-6 shadow-md border border-cream-200 mb-6">
              <h3 className="text-lg font-bold text-brown-800 mb-4">Buscar leituras</h3>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Buscar por santo, leitura, data ou tema..."
                  className="flex-1 px-4 py-3 border border-cream-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-colors"
                  style={{ outline: 'none' }}
                />
                <Button onClick={handleSearch} disabled={searching}>
                  <Search size={18} />
                  {searching ? 'Buscando...' : 'Buscar'}
                </Button>
              </div>
            </div>

            {searchResults.length > 0 && (
              <div className="space-y-4">
                {searchResults.map(r => (
                  <button
                    key={r.id}
                    onClick={() => navigateToReading(r.date)}
                    className="w-full bg-white rounded-2xl p-5 shadow-md border border-cream-200 text-left hover:shadow-lg transition-shadow"
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-gold-100 rounded-xl p-3">
                        <Calendar size={20} className="text-gold-600" />
                      </div>
                      <div>
                        <p className="text-sm text-brown-500">{formatDateDisplay(r.date)}</p>
                        <p className="font-bold text-brown-800">{r.liturgical_title || r.weekday}</p>
                        {r.saint_name && (
                          <p className="text-sm text-brown-600">{r.saint_name}</p>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {searchResults.length === 0 && searchQuery && !searching && (
              <div className="bg-white rounded-2xl p-8 shadow-md border border-cream-200 text-center">
                <Search size={40} className="text-cream-300 mx-auto mb-3" />
                <p className="text-brown-500">Nenhum resultado encontrado. Tente buscar por outro termo.</p>
              </div>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default Devocional;
